import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { Play, X } from "lucide-react";

const videos = [
  {
    id: "0Blz-0swtyI",
    title: "SHIFT HAPPENS Mobility Series EP 01",
    description: "Introduction to the mobility series featuring industry leaders.",
  },
  {
    id: "Vnrfa4I2pzU",
    title: "The Mobility Industry - Vision Episode 2",
    description: "Exploring the vision for the future of mobility industry.",
  },
  {
    id: "Y-Lfqotkv94",
    title: "Co-Creating Mobility 2030",
    description: "How India & the UK are building together for sustainable mobility.",
  },
  {
    id: "Vwe_-Y3Kz6o",
    title: "The Road Ahead: India Mobility Partnerships",
    description: "Avnish Malhotra on future mobility partnerships with India.",
  },
  {
    id: "Q5fnO79_Mck",
    title: "Inside Indian Manufacturing Leadership",
    description: "Ulhas Joshi from Otter Controls India shares insights.",
  },
  {
    id: "0J0t7DTMpq0",
    title: "Vibhas Ambekar - Helical India Spotlight",
    description: "A SHIFT HAPPENS spotlight on Helical India.",
  },
];

const InitiativesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [activeVideo, setActiveVideo] = useState(null);

  const getYouTubeThumbnail = (videoId) => {
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  };

  return (
    <section
      id="initiatives"
      ref={ref}
      data-testid="initiatives-section"
      className="py-20 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <span className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-700 mb-4 block">
            Initiatives
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-stone-900 mb-4 tracking-tight">
            Initiatives Executed
          </h2>
          <p className="text-stone-600 leading-relaxed">
            Discover our flagship programmes bringing together stakeholders from
            around the world to advance sustainable mobility.
          </p>
        </motion.div>

        {/* Video Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {videos.map((video, index) => (
            <motion.div
              key={video.id}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              className="group"
            >
              <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 hover:border-emerald-200">
                {/* Video Thumbnail */}
                <div className="relative aspect-video bg-stone-900 cursor-pointer overflow-hidden"
                     onClick={() => setActiveVideo(video.id)}
                     data-testid={`video-thumbnail-${index}`}
                >
                  <img
                    src={getYouTubeThumbnail(video.id)}
                    alt={video.title}
                    className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-300"
                    loading="lazy"
                    onError={(e) => {
                      e.target.src = `https://img.youtube.com/vi/${video.id}/hqdefault.jpg`;
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-stone-900/60 via-transparent to-stone-900/20" />
                  
                  {/* Play Button */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-14 h-14 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-xl group-hover:scale-110 group-hover:bg-white transition-all duration-300">
                      <Play className="w-6 h-6 text-emerald-700 ml-1" fill="currentColor" />
                    </div>
                  </div>

                  {/* Badge */}
                  {index === 0 && (
                    <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 bg-emerald-600 text-white text-xs font-semibold rounded-full">
                      <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                      Featured
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-5">
                  <h3 className="font-heading text-lg font-semibold text-stone-900 mb-2 group-hover:text-emerald-700 transition-colors">
                    {video.title}
                  </h3>
                  <p className="text-stone-500 text-sm leading-relaxed">
                    {video.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* More Content Note */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.8, duration: 0.5 }}
          className="text-center mt-12"
        >
          <p className="text-stone-500 text-sm">
            More case studies and initiatives coming soon.
          </p>
        </motion.div>
      </div>

      {/* Video Modal */}
      {activeVideo && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={() => setActiveVideo(null)}
          data-testid="video-modal"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="relative w-full max-w-4xl aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <iframe
              src={`https://www.youtube.com/embed/${activeVideo}?autoplay=1&rel=0`}
              title="YouTube video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
            />
            <button
              onClick={() => setActiveVideo(null)}
              className="absolute top-4 right-4 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors"
              data-testid="close-video-modal"
            >
              <X className="w-5 h-5" />
            </button>
          </motion.div>
        </div>
      )}
    </section>
  );
};

export default InitiativesSection;
